<template>
  <div class="md-pagelist">
    <div v-if="listTitle" class="pagelist-title">{{ listTitle }}</div>
    <div v-if="loading" class="pagelist-loading">
      <i class="el-icon-loading"></i>
      <span>{{ $t('workbench.loading') || '加载中...' }}</span>
    </div>
    <div v-else-if="error" class="pagelist-error">
      <i class="el-icon-warning"></i>
      <span>{{ error }}</span>
    </div>
    <template v-else>
      <el-table :data="displayData" border size="small" style="width: 100%">
        <el-table-column
          v-for="(col, idx) in columnsParsed"
          :key="idx"
          :label="col.label"
          :prop="col.key"
          show-overflow-tooltip
        >
          <template slot-scope="{ row }">
            <el-tag
              v-if="col.tag && col.tagMap"
              :type="getTagType(col.tagMap, row, col)"
              :color="getTagColor(col.tagMap, row, col)"
              size="small"
              :style="
                getTagColor(col.tagMap, row, col)
                  ? {
                      borderColor: getTagColor(col.tagMap, row, col),
                      color: '#fff',
                    }
                  : {}
              "
              >{{ getTagLabel(col.tagMap, row, col) }}</el-tag
            >
            <span v-else>{{ getCellValue(col, row) }}</span>
          </template>
        </el-table-column>
        <el-table-column
          v-if="showOperationColumn"
          :label="$t('workbench.operation')"
          width="120"
        >
          <template slot-scope="{ row }">
            <el-button
              v-for="(btn, bidx) in operationButtons"
              :key="bidx"
              type="primary"
              size="mini"
              :disabled="operated"
              @click="handleOperation(row, btn)"
              >{{ btn.buttonLabel || $t('workbench.operation') }}</el-button
            >
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        v-if="showPagination"
        :current-page.sync="currentPage"
        :page-size="currentPageSize"
        background
        layout="total, prev, pager, next"
        :total="apiTotal || staticData.length"
        class="pagelist-pagination"
        @current-change="handlePageChange"
      />
    </template>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'PageList',
  props: {
    title: { type: String, default: '' },
    columns: { type: String, default: '' },
    data: { type: String, default: '' },
    datasource: { type: String, default: '' },
    action: { type: String, default: '' },
    operationColumn: { type: String, default: '' },
    pageColumns: { type: String, default: '' },
    pageSize: { type: Number, default: 10 },
    isHistory: { type: Boolean, default: false },
    messageId: { type: String, default: '' },
    _rawJson: { type: String, default: '' },
    _componentKey: { type: String, default: '' }
  },
  data() {
    return {
      currentPage: 1,
      loading: false,
      error: '',
      apiData: [],
      apiTotal: 0,
      operated: false
    }
  },
  computed: {
    parsedConfig() {
      return this._parseJson(this._rawJson) || {}
    },
    listTitle() {
      return this.title || this.parsedConfig.title || ''
    },
    columnsParsed() {
      const cols =
        this._parseJson(this.columns) || this.parsedConfig.columns || []
      return Array.isArray(cols) ? cols : []
    },
    datasourceParsed() {
      return (
        this._parseJson(this.datasource) || this.parsedConfig.datasource || null
      )
    },
    operationColumnParsed() {
      const raw =
        this._parseJson(this.operationColumn) ||
        this.parsedConfig.operationColumn ||
        null
      if (Array.isArray(raw)) return raw
      if (raw && typeof raw === 'object') return [raw]
      return []
    },
    pageColumnsParsed() {
      return (
        this._parseJson(this.pageColumns) ||
        this.parsedConfig.pageColumns ||
        null
      )
    },
    noPagination() {
      return !this.pageColumnsParsed
    },
    isApiMode() {
      return this.datasourceParsed && this.datasourceParsed.type === 'api'
    },
    staticData() {
      const d = this._parseJson(this.data) || this.parsedConfig.data || []
      return Array.isArray(d) ? d : []
    },
    displayData() {
      if (this.isApiMode) return this.apiData
      const start = (this.currentPage - 1) * this.pageSize
      return this.staticData.slice(start, start + this.pageSize)
    },
    currentPageSize() {
      if (
        this.isApiMode &&
        this.datasourceParsed.api &&
        this.datasourceParsed.api.params
      ) {
        const ps = this.datasourceParsed.api.params.pageSize
        return ps ? parseInt(ps.value || ps) : this.pageSize
      }
      return this.pageSize
    },
    showPagination() {
      if (this.noPagination) return false
      if (this.isApiMode) return this.apiTotal > this.currentPageSize
      return this.staticData.length > this.pageSize
    },
    showOperationColumn() {
      return this.operationButtons.length > 0
    },
    operationButtons() {
      return this.operationColumnParsed.filter((btn) => btn.show !== false)
    },
    operationColumnLabel() {
      return this.$t('workbench.operation')
    },
    alreadyOperated() {
      const action = this.action || this.parsedConfig.action || ''
      if (!action) return false
      const messages = this.$store.state.cosmoClaw.messages || []
      // 找到当前消息的索引，只检查该消息之后的 action
      let startIdx = -1
      if (this.messageId) {
        for (let i = 0; i < messages.length; i++) {
          if (messages[i].id === this.messageId) {
            startIdx = i
            break
          }
        }
        // messageId 指定但未找到，说明不在当前会话中，不视为已操作
        if (startIdx === -1) return false
      } else {
        startIdx = 0
      }
      for (let i = startIdx; i < messages.length; i++) {
        const msg = messages[i]
        if (msg.type !== 'assistant') continue
        if (!(msg.content || '').includes(`"${action}"`)) continue
        for (let j = i + 1; j < messages.length; j++) {
          const nextMsg = messages[j]
          if (
            nextMsg.type === 'tool' ||
            nextMsg.type === 'thinking' ||
            nextMsg.type === 'toolCall'
          ) {
            continue
          }
          if (nextMsg.type === 'assistant') break
          if (nextMsg.type === 'user') {
            if ((nextMsg.content || '').startsWith('[cardlist-action]')) {
              return true
            }
            break
          }
        }
      }
      return false
    }
  },
  mounted() {
    if (this.alreadyOperated) {
      this.operated = true
    }
    if (this.isApiMode) {
      this.fetchData()
    }
  },
  methods: {
    async fetchData() {
      const ds = this.datasourceParsed
      if (!ds || !ds.api) return
      const api = ds.api
      const method = (api.method || 'GET').toUpperCase()
      const url = api.url || ''

      // Build params object from api.params definitions
      const params = {}
      if (api.params) {
        Object.keys(api.params).forEach((key) => {
          const param = api.params[key]
          params[key] = typeof param === 'object' ? param.value : param
        })
      }
      // Override pageNo with current page
      if ('pageNo' in params) params.pageNo = String(this.currentPage)
      if ('pageNum' in params) params.pageNum = String(this.currentPage)

      // Determine if URL is absolute or relative
      const isAbsoluteUrl = /^https?:\/\//i.test(url)

      this.loading = true
      this.error = ''

      try {
        const config = { url, method }

        if (isAbsoluteUrl) {
          config.baseURL = ''
        }

        if (method === 'GET') {
          config.params = params
        } else {
          config.data = params
        }
        const response = await axios(config)
        const resData = response.data
        const payload = resData.data || resData.result

        // 无分页模式：api 直接返回数组
        if (this.noPagination) {
          const list = Array.isArray(payload)
            ? payload
            : Array.isArray(resData)
              ? resData
              : []
          this.apiData = list
          this.apiTotal = list.length
        } else if (resData && resData.code === 0 && payload) {
          const pageData = payload
          const pc = this.pageColumnsParsed
          const recordsKey = (pc && pc.records) || 'records'
          const totalKey = (pc && pc.total) || 'total'
          const list = pageData[recordsKey] || pageData.list || pageData.rows
          this.apiData = Array.isArray(list)
            ? list
            : Array.isArray(pageData)
              ? pageData
              : []
          this.apiTotal = parseInt(
            pageData[totalKey] || pageData.totalCount || this.apiData.length
          )
        } else if (Array.isArray(resData)) {
          this.apiData = resData
          this.apiTotal = resData.length
        } else {
          this.apiData = []
          this.apiTotal = 0
          this.error =
            (resData && (resData.msg || resData.message)) ||
            this.$t('workbench.dataLoadFailed')
        }
      } catch (e) {
        this.error = e.message || this.$t('workbench.requestFailedSimple')
        this.apiData = []
        this.apiTotal = 0
      } finally {
        this.loading = false
      }
    },
    handlePageChange(page) {
      this.currentPage = page
      if (this.isApiMode) {
        this.fetchData()
      }
    },
    handleOperation(row, btn) {
      if (this.operated) return
      this.operated = true
      const prefix = btn.next_prefix || ''
      const action = this.action || this.parsedConfig.action || ''
      const content = prefix
        ? `${prefix}：${JSON.stringify(row)}`
        : JSON.stringify(row)
      const message = `[cardlist-action]${action}:${content}`
      document.dispatchEvent(
        new CustomEvent('md-card-action', {
          detail: { action, value: JSON.stringify(row), message }
        })
      )
    },
    getCellValue(col, row) {
      if (col.keys && Array.isArray(col.keys)) {
        const values = col.keys
          .map((k) => row[k])
          .filter((v) => v !== undefined && v !== null)
        if (values.length === 0) return ''
        const joined = values.join(col.separator || '')
        const prepend = col.prepend || ''
        const append = col.append || ''
        return prepend + joined + append
      }
      return row[col.key] !== undefined ? row[col.key] : ''
    },
    // 检测 tagMap 是否为新格式：{ code: { label, type } }
    _isRichTagMap(tagMap) {
      if (!tagMap) return false
      const values = Object.values(tagMap)
      return (
        values.length > 0 &&
        typeof values[0] === 'object' &&
        values[0] !== null &&
        'label' in values[0]
      )
    },
    getTagLabel(tagMap, row, col) {
      if (!tagMap) return ''
      const rawVal = this.getCellValue(col, row)
      if (!rawVal) return ''
      if (!this._isRichTagMap(tagMap)) return rawVal
      const entry = tagMap[rawVal]
      return entry ? entry.label : rawVal
    },
    getTagType(tagMap, row, col) {
      if (!tagMap) return ''
      const rawVal = this.getCellValue(col, row)
      if (!rawVal) return ''
      if (this._isRichTagMap(tagMap)) {
        const entry = tagMap[rawVal]
        return entry && entry.type ? entry.type : ''
      }
      const mapped = tagMap[rawVal] || ''
      const validTypes = ['primary', 'success', 'error', 'warning', 'info']
      return validTypes.includes(mapped) ? mapped : ''
    },
    getTagColor(tagMap, row, col) {
      if (!tagMap) return ''
      const rawVal = this.getCellValue(col, row)
      if (!rawVal) return ''
      if (this._isRichTagMap(tagMap)) {
        const entry = tagMap[rawVal]
        return entry && entry.color ? entry.color : ''
      }
      const mapped = tagMap[rawVal] || ''
      const validTypes = ['primary', 'success', 'error', 'warning', 'info']
      return validTypes.includes(mapped) ? '' : mapped
    },
    _parseJson(value) {
      if (!value) return null
      if (typeof value === 'object') return value
      if (typeof value !== 'string') return null
      const cleaned = value.replace(/\n/g, '')
      try {
        return JSON.parse(cleaned)
      } catch {
        try {
          return JSON.parse(
            cleaned.replace(/'/g, '"').replace(/,\s*([}\]])/g, '$1')
          )
        } catch {
          return null
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.md-pagelist {
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  padding: 16px;
  margin: 8px 0;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.pagelist-title {
  font-size: 14px;
  font-weight: 600;
  color: #1f2329;
  margin-bottom: 12px;
}

.pagelist-loading,
.pagelist-error {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px 0;
  font-size: 13px;
}

.pagelist-loading {
  color: #409eff;
  i {
    font-size: 16px;
  }
}

.pagelist-error {
  color: #f5222d;
  i {
    font-size: 16px;
  }
}

.pagelist-pagination {
  margin-top: 12px;
  text-align: right;

  ::v-deep ul {
    padding-left: 0 !important;
    margin: 0 !important;
  }
}
</style>
